import { useMatch } from 'react-router-dom'
import { Header } from '../Header'
import { BottomNav } from '../BottomNav'
import { SignInBanner } from '../SignInBanner'
import { useAuth } from '@/hooks/useAuth'
import { useScrollMemory } from '@/hooks/useScrollMemory'
import type { LayoutProps } from './Layout.types'

export function Layout({ children }: LayoutProps) {
    const { isAuthenticated } = useAuth()
    const postDetailMatch = useMatch('/profile/:identifier/post/:postId')
    const articleDetailMatch = useMatch('/profile/:did/article/:rkey')
    const publicationMatch = useMatch('/publication/:did/:rkey')
    const profileMatch = useMatch('/profile/:identifier')
    const articlesMatch = useMatch('/articles')
    useScrollMemory()

    // Hide header on post/article/publication detail pages for authenticated users (they have a back button)
    const hideHeader =
        isAuthenticated &&
        (postDetailMatch !== null || articleDetailMatch !== null || publicationMatch !== null)

    // Public share-link routes that logged-out users can view
    const isPublicRoute =
        !isAuthenticated && (!!postDetailMatch || !!profileMatch || !!publicationMatch)

    const showHeader = (isAuthenticated && !hideHeader) || isPublicRoute

    const mainClass = isAuthenticated
        ? hideHeader
            ? 'pb-24'
            : 'pt-16 pb-24'
        : isPublicRoute
          ? 'pt-16 pb-16'
          : 'p-6 justify-center'

    // The Articles route widens above Tailwind's default `sm` breakpoint
    // (640px), which must stay equal to ARTICLE_GRID_MIN_WIDTH so the
    // single-column list keeps today's 480px cap right up to where the grid
    // takes over. 1200 is ARTICLE_GRID_MAX_WIDTH — Tailwind arbitrary values
    // must be literals, hence the constants are duplicated here rather than
    // imported.
    const isWideArticles = isAuthenticated && articlesMatch !== null
    const contentColumnClass = isWideArticles
        ? 'w-full max-w-[480px] sm:max-w-[1200px]'
        : 'w-full max-w-[480px]'

    return (
        <div className="min-h-screen flex flex-col">
            {showHeader && <Header />}
            <main
                className={`flex-1 flex flex-col items-center transition-all duration-300 ${mainClass}`}
            >
                {/*
                  `transition-none` is load-bearing, not decoration. This project
                  is on Tailwind v3 with no `tailwindcss-animate` plugin, so
                  `animate-in` / `fade-in` / `slide-in-from-bottom-4` compile to
                  nothing at all and `duration-500` is Tailwind's *transition*
                  utility -- it emits only `transition-duration: 500ms`, never
                  `animation-duration`. Since the CSS initial value of
                  `transition-property` is `all`, that lone duration turned every
                  animatable property on this div into a 500ms transition,
                  including `max-width`. Once `contentColumnClass` started
                  differing per route (below), navigating /articles -> an article
                  smoothly animated max-width 1200px -> 480px on top of the route
                  change -- confirmed via getAnimations(): a CSSTransition with
                  transitionProperty "max-width". `transition-none` sets
                  `transition-property: none`, which wins regardless of duration
                  because it is a different declaration.
                */}
                <div
                    className={`${contentColumnClass} animate-in fade-in slide-in-from-bottom-4 duration-500 transition-none`}
                >
                    {children}
                </div>
            </main>
            {isAuthenticated && <BottomNav />}
            {isPublicRoute && <SignInBanner />}
        </div>
    )
}
